import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect } from "react";
import { getAdminProducts } from "../../actions/productActions";
import { getAllOrders } from "../../actions/orderAction";
import { getUsers } from "../../actions/userActions";
import Sidebar from "./Sidebar";
import { MdInventory, MdPeople, MdShoppingCart, MdPayments, MdArrowForward } from "react-icons/md";

const Dashboard = () => {
    const dispatch = useDispatch();
    const { products = [] } = useSelector(state => state.productsState);
    const { adminOrders = [] } = useSelector(state => state.orderState);
    const { users = [] } = useSelector(state => state.authState);

    useEffect(() => {
        dispatch(getAdminProducts());
        dispatch(getAllOrders());
        dispatch(getUsers());
    }, [dispatch]);

    const calculateTotalAmount = () => {
        return adminOrders.reduce((total, order) => {
            // Priority: stored totalPrice (new orders) -> manual calculation (legacy orders)
            if (order.totalPrice && order.totalPrice > 0) {
                return total + order.totalPrice;
            }
            // Fallback calculation for old orders
            const orderSum = order.orderItems.reduce((acc, item) => {
                const itemPrice = (item.price / 8 * item.weight) + (item.price * item.makingrate / 100);
                return acc + (itemPrice * item.quantity);
            }, 0);
            return total + Math.round(orderSum);
        }, 0);
    };

    const dashboardCards = [
        { 
            title: "Total Revenue", 
            value: `Rs ${calculateTotalAmount().toLocaleString()}`, 
            link: "/admin/orders", 
            icon: <MdPayments />,
            bg: "bg-gray-900 border-yellow-600/30" 
        },
        { 
            title: "Inventory", 
            value: products.length, 
            link: "/admin/products", 
            icon: <MdInventory />,
            bg: "bg-white border-gray-100" 
        },
        { 
            title: "Active Orders", 
            value: adminOrders.length, 
            link: "/admin/orders", 
            icon: <MdShoppingCart />,
            bg: "bg-white border-gray-100" 
        },
        { 
            title: "Registered Users", 
            value: users.length, 
            link: "/admin/users", 
            icon: <MdPeople />,
            bg: "bg-white border-gray-100" 
        }
    ];

    return (
        <div className="flex bg-gray-50 min-h-screen">
            <Sidebar />
            
            <div className="flex-1 p-8 lg:p-12 overflow-x-hidden">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="mb-12">
                        <h1 className="text-4xl font-serif font-bold text-gray-900 mb-2">Boutique Insights</h1>
                        <p className="text-gray-500 text-sm font-light">Comprehensive overview of your luxury store performance.</p>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {dashboardCards.map((card, index) => (
                            <div key={index} className={`${card.bg} p-8 rounded-[2.5rem] border shadow-sm transition-all duration-500 hover:shadow-xl hover:-translate-y-1 group`}>
                                <div className="flex items-center justify-between mb-8">
                                    <div className={`p-4 rounded-2xl text-2xl ${card.icon === dashboardCards[0].icon ? 'bg-yellow-500 text-gray-900' : 'bg-gray-50 text-gray-400 group-hover:text-yellow-600 group-hover:bg-yellow-50'} transition-colors`}>
                                        {card.icon}
                                    </div>
                                    <Link to={card.link} className={`${card.icon === dashboardCards[0].icon ? 'text-gray-400 hover:text-white' : 'text-gray-300 hover:text-gray-900'} transition-all`}>
                                        <MdArrowForward className="text-xl" />
                                    </Link>
                                </div>
                                <div className={`${card.icon === dashboardCards[0].icon ? 'text-gray-400' : 'text-gray-400'} text-[10px] font-bold uppercase tracking-[0.2em] mb-1`}>
                                    {card.title}
                                </div>
                                <div className={`${card.icon === dashboardCards[0].icon ? 'text-white' : 'text-gray-900'} text-3xl font-serif font-bold`}>
                                    {card.value}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Quick Access Info */}
                    <div className="mt-16 bg-white rounded-[3rem] p-12 border border-gray-100 shadow-sm flex flex-col md:flex-row items-center justify-between gap-8">
                        <div className="space-y-2 text-center md:text-left">
                            <h2 className="text-xl font-bold text-gray-900">System Integrity Verified</h2>
                            <p className="text-gray-400 text-sm font-light uppercase tracking-widest">Connected Backend Node: Multi-Store Hub</p>
                        </div>
                        <div className="flex gap-4">
                            <Link to="/admin/products/create" className="bg-gray-900 text-white px-10 py-5 rounded-2xl text-[10px] font-bold uppercase tracking-widest hover:bg-yellow-600 transition-all shadow-lg hover:shadow-yellow-600/20 active:scale-95">
                                Add New Collection
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;